import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import useSWR from 'swr';
import { useNotification } from '../context/NotificationContext';
import {
  getNetworkFollowers,
  getNetworkFollowing,
  getPendingConnections,
  denyPendingConnection,
  getNetworkSuggestions,
  browseNetwork,
  browseProductions,
  searchByAvailability,
  getMediaUrl,
  followUser,
  uploadFile,
} from '../services/api';
import { AvailabilityBadge } from '../components/AvailabilityTimeline';
import '../components/AvailabilityTimeline.css';
import './Network.css';

const fetchNetworkOverview = async () => {
  const [followersRes, followingRes, pendingRes, suggestionsRes] = await Promise.all([
    getNetworkFollowers(),
    getNetworkFollowing(),
    getPendingConnections(),
    getNetworkSuggestions(40),
  ]);

  return {
    followers: followersRes.data || [],
    following: followingRes.data || [],
    pending: pendingRes.data || [],
    suggestions: suggestionsRes.data?.suggestions || [],
  };
};

function Network() {
  const { showError, showSuccess } = useNotification();
  const [pendingConnections, setPendingConnections] = useState([]);
  const [networkTab, setNetworkTab] = useState('grow');
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectPerson, setConnectPerson] = useState(null);
  const [connectMessage, setConnectMessage] = useState('');
  const [connectMedia, setConnectMedia] = useState(null);
  const [connectMediaType, setConnectMediaType] = useState(null);
  const [connectMediaFile, setConnectMediaFile] = useState(null);
  const [sendingConnection, setSendingConnection] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [productionResults, setProductionResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [availFrom, setAvailFrom] = useState('');
  const [availTo, setAvailTo] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterRef = useRef(null);
  const [followedIds, setFollowedIds] = useState(new Set());
  const connectModalRef = useRef(null);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const {
    data: networkData,
    error: networkError,
    isLoading: loading,
    mutate: refreshConnections,
  } = useSWR('network-overview', fetchNetworkOverview);

  const followers = networkData?.followers || [];
  const following = networkData?.following || [];
  const suggestions = networkData?.suggestions || [];

  useEffect(() => {
    if (networkData?.pending) {
      setPendingConnections(networkData.pending);
    }
  }, [networkData]);

  useEffect(() => {
    if (!networkError) return;
    console.error('Error loading connections:', networkError);
    showError('Failed to load connections. Please try again.');
  }, [networkError, showError]);

  const handleSearch = useCallback(async (query, filter, status, from, to) => {
    if (!query.trim() && !filter && !status && !from) {
      setSearchResults([]);
      setProductionResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const promises = [];

      // If filtering by availability status or dates, use availability search
      if (status || (from && to)) {
        promises.push(
          searchByAvailability(from || undefined, to || undefined, status || undefined)
        );
      } else {
        promises.push(
          browseNetwork({
            q: query.trim() || undefined,
            profession: filter || undefined,
            limit: 20,
          })
        );
      }

      if (query.trim()) {
        promises.push(browseProductions(query.trim(), 5));
      }
      const [peopleRes, prodRes] = await Promise.all(promises);
      setSearchResults(peopleRes.data || []);
      setProductionResults(prodRes ? prodRes.data || [] : []);
    } catch (error) {
      console.error('Error searching:', error);
    }
    setSearchLoading(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(
      () => handleSearch(searchQuery, activeFilter, statusFilter, availFrom, availTo),
      300
    );
    return () => clearTimeout(timer);
  }, [searchQuery, activeFilter, statusFilter, availFrom, availTo, handleSearch]);

  const handleFilterClick = (filter) => {
    setActiveFilter(activeFilter === filter ? null : filter);
    setShowFilterDropdown(false);
  };

  // Close filter dropdown on click outside
  useEffect(() => {
    if (!showFilterDropdown) return;
    const handleClick = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilterDropdown(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showFilterDropdown]);

  // Close modals when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (connectModalRef.current && !connectModalRef.current.contains(event.target)) {
        closeConnectModal();
      }
    };

    if (showConnectModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showConnectModal]);

  const openConnectModal = (person) => {
    setConnectPerson(person);
    setShowConnectModal(true);
    setConnectMessage('');
    setConnectMedia(null);
    setConnectMediaType(null);
  };

  const closeConnectModal = () => {
    setShowConnectModal(false);
    setConnectPerson(null);
    setConnectMessage('');
    setConnectMedia(null);
    setConnectMediaType(null);
    setConnectMediaFile(null);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setConnectMediaFile(file);
      setConnectMediaType('image');
      const reader = new FileReader();
      reader.onload = (event) => setConnectMedia(event.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleVideoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setConnectMediaFile(file);
      setConnectMediaType('video');
      const reader = new FileReader();
      reader.onload = (event) => setConnectMedia(event.target.result);
      reader.readAsDataURL(file);
    }
  };

  const removeMedia = () => {
    setConnectMedia(null);
    setConnectMediaType(null);
    setConnectMediaFile(null);
  };

  const handleSendConnection = async () => {
    if (!connectPerson) return;
    setSendingConnection(true);
    try {
      // Upload media if attached
      let mediaUrl = null;
      if (connectMediaFile) {
        const uploadRes = await uploadFile(connectMediaFile);
        mediaUrl = uploadRes.data?.url;
      }

      // Build message with media
      let message = connectMessage.trim() || undefined;
      if (mediaUrl) {
        const mediaTag =
          connectMediaType === 'image' ? `[image:${mediaUrl}]` : `[video:${mediaUrl}]`;
        message = message ? `${message}\n${mediaTag}` : mediaTag;
      }

      await followUser(connectPerson.id, message);
      setFollowedIds((prev) => new Set([...prev, connectPerson.id]));
      showSuccess(
        message
          ? `Request sent to ${connectPerson.full_name}`
          : `Following ${connectPerson.full_name}`
      );
      refreshConnections();
      closeConnectModal();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to connect');
    }
    setSendingConnection(false);
  };

  const handleConnectBack = async (person) => {
    try {
      await followUser(person.id);
      setFollowedIds((prev) => new Set([...prev, person.id]));
      setPendingConnections((prev) => prev.filter((p) => p.id !== person.id));
      showSuccess(`You're now connected with ${person.full_name}`);
      refreshConnections();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to connect');
    }
  };

  const handleDenyPending = async (person) => {
    try {
      await denyPendingConnection(person.id);
      setPendingConnections((prev) => prev.filter((p) => p.id !== person.id));
      showSuccess('Request dismissed');
      refreshConnections();
    } catch (error) {
      showError(error.response?.data?.error || 'Failed to dismiss request');
    }
  };

  return (
    <div className="network-page">
      <div className="network-inner">
        {/* Connect Modal */}
        {showConnectModal && connectPerson && (
          <div className="connect-modal-overlay">
            <div className="connect-modal" ref={connectModalRef}>
              <div className="connect-modal-header">
                <div className="connect-modal-person">
                  <div className="connect-modal-avatar">
                    {connectPerson.full_name?.charAt(0) || '?'}
                  </div>
                  <div className="connect-modal-person-info">
                    <div className="connect-modal-name">{connectPerson.full_name}</div>
                    <div className="connect-modal-title">{connectPerson.job_title}</div>
                  </div>
                </div>
                <button
                  type="button"
                  className="connect-modal-close"
                  onClick={closeConnectModal}
                  aria-label="Close connection modal"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>

              <div className="connect-modal-message">
                <label className="connect-modal-label">Add a message (optional)</label>
                <textarea
                  className="connect-modal-textarea"
                  placeholder="Hi! I'd love to connect with you..."
                  value={connectMessage}
                  onChange={(e) => setConnectMessage(e.target.value)}
                />
              </div>

              <div className="connect-modal-media">
                <label className="connect-modal-label">Attach media (optional)</label>
                <div className="connect-modal-media-btns">
                  <button
                    type="button"
                    className="connect-modal-media-btn"
                    onClick={() => photoInputRef.current?.click()}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                      <polyline points="21 15 16 10 5 21"></polyline>
                    </svg>
                    Photo
                  </button>
                  <button
                    type="button"
                    className="connect-modal-media-btn"
                    onClick={() => videoInputRef.current?.click()}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polygon points="23 7 16 12 23 17 23 7"></polygon>
                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                    </svg>
                    Video
                  </button>
                </div>
                <input
                  type="file"
                  ref={photoInputRef}
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handlePhotoUpload}
                  aria-label="Connection photo"
                />
                <input
                  type="file"
                  ref={videoInputRef}
                  accept="video/*"
                  style={{ display: 'none' }}
                  onChange={handleVideoUpload}
                  aria-label="Connection video"
                />

                {connectMedia && (
                  <div className="connect-modal-preview">
                    {connectMediaType === 'image' ? (
                      <img src={connectMedia} alt="Preview" className="connect-modal-preview-img" />
                    ) : (
                      <video src={connectMedia} className="connect-modal-preview-video" controls />
                    )}
                    <button
                      type="button"
                      className="connect-modal-preview-remove"
                      onClick={removeMedia}
                      aria-label="Remove attached media"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              <div className="connect-modal-actions">
                <button type="button" className="connect-modal-cancel" onClick={closeConnectModal}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="connect-modal-send"
                  onClick={handleSendConnection}
                  disabled={sendingConnection}
                >
                  {sendingConnection ? 'Sending...' : 'Send Connection'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Hero Card */}
        <div className="network-hero-card">
          <div className="network-hero-content">
            <h1 className="network-hero-title">Your Network.</h1>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="network-search">
          <div className="network-search-input-wrapper">
            <svg
              className="network-search-icon"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              className="network-search-input"
              placeholder="Search people, theaters, productions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {(searchQuery || activeFilter || statusFilter || availFrom) && (
              <button
                className="network-search-clear"
                onClick={() => {
                  setSearchQuery('');
                  setActiveFilter(null);
                  setStatusFilter(null);
                  setAvailFrom('');
                  setAvailTo('');
                }}
                aria-label="Clear network search"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
            <div className="network-filter-wrap" ref={filterRef}>
              <button
                className={`network-filter-btn ${activeFilter || statusFilter || availFrom ? 'has-filter' : ''}`}
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="4" y1="6" x2="20" y2="6" />
                  <line x1="8" y1="12" x2="16" y2="12" />
                  <line x1="11" y1="18" x2="13" y2="18" />
                </svg>
                {activeFilter || statusFilter || availFrom ? 'Filtered' : 'Filters'}
              </button>
              {showFilterDropdown && (
                <div className="network-filter-dropdown">
                  <div className="network-filter-section-label">Profession</div>
                  {[
                    'Actor',
                    'Singer',
                    'Dancer',
                    'Director',
                    'Stage Manager',
                    'Choreographer',
                    'Music Director',
                    'Casting Director',
                    'Producer',
                    'Playwright',
                    'Composer',
                    'Designer',
                  ].map((f) => (
                    <button
                      key={f}
                      className={`network-filter-option ${activeFilter === f ? 'active' : ''}`}
                      onClick={() => handleFilterClick(f)}
                    >
                      {f}
                    </button>
                  ))}

                  <div className="network-filter-section-label" style={{ marginTop: '8px' }}>
                    Status
                  </div>
                  {[
                    { id: 'open_to_auditions', label: 'Open to Auditions' },
                    { id: 'currently_casting', label: 'Currently Casting' },
                    { id: 'available_for_hire', label: 'Available for Hire' },
                    { id: 'seeking_collaborators', label: 'Seeking Collaborators' },
                  ].map((s) => (
                    <button
                      key={s.id}
                      className={`network-filter-option ${statusFilter === s.id ? 'active' : ''}`}
                      onClick={() => {
                        setStatusFilter(statusFilter === s.id ? null : s.id);
                      }}
                    >
                      {s.label}
                    </button>
                  ))}

                  <div className="network-filter-section-label" style={{ marginTop: '8px' }}>
                    Available Between
                  </div>
                  <div className="network-filter-dates">
                    <input
                      type="date"
                      value={availFrom}
                      onChange={(e) => setAvailFrom(e.target.value)}
                    />
                    <span>to</span>
                    <input
                      type="date"
                      value={availTo}
                      onChange={(e) => setAvailTo(e.target.value)}
                    />
                  </div>

                  {(activeFilter || statusFilter || availFrom) && (
                    <button
                      className="network-filter-clear"
                      onClick={() => {
                        setActiveFilter(null);
                        setStatusFilter(null);
                        setAvailFrom('');
                        setAvailTo('');
                        setShowFilterDropdown(false);
                      }}
                    >
                      Clear all filters
                    </button>
                  )}
                </div>
              )}
            </div>
            {/* Search Dropdown */}
            {(searchQuery || activeFilter || statusFilter || availFrom) && (
              <div className="network-search-dropdown">
                {searchLoading ? (
                  <div className="network-search-dropdown-status">Searching...</div>
                ) : searchResults.length === 0 && productionResults.length === 0 ? (
                  <div className="network-search-dropdown-status">
                    No results found{activeFilter ? ` for "${activeFilter}"` : ''}
                  </div>
                ) : (
                  <>
                    {productionResults.length > 0 && (
                      <>
                        <div className="network-search-dropdown-label">Productions</div>
                        {productionResults.map((prod, i) => (
                          <Link
                            key={i}
                            to={`/production?name=${encodeURIComponent(prod.production)}${prod.company ? `&company=${encodeURIComponent(prod.company)}` : ''}`}
                            className="network-search-result"
                          >
                            <div className="network-search-result-info">
                              <div className="network-production-icon">
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                >
                                  <path d="M2 16.1A5 5 0 0 1 5.9 20M2 12.05A9 9 0 0 1 9.95 20M2 8V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-6" />
                                  <line x1="2" y1="20" x2="2.01" y2="20" />
                                </svg>
                              </div>
                              <div>
                                <span className="network-search-result-name">
                                  {prod.production}
                                </span>
                                <span className="network-search-result-username">
                                  {prod.company || ''}
                                  {prod.people_count ? ` · ${prod.people_count} people` : ''}
                                </span>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </>
                    )}
                    {searchResults.length > 0 && productionResults.length > 0 && (
                      <div className="network-search-dropdown-label">People</div>
                    )}
                    {searchResults.map((person) => (
                      <Link
                        key={person.id}
                        to={`/profile/${person.id}`}
                        className="network-search-result"
                      >
                        <div className="network-search-result-info">
                          <div className="network-search-result-avatar">
                            {person.profile_image ? (
                              <img src={getMediaUrl(person.profile_image)} alt="" />
                            ) : (
                              (person.full_name || person.username)?.charAt(0).toUpperCase()
                            )}
                          </div>
                          <div>
                            <span className="network-search-result-name">
                              {person.full_name || person.username}
                            </span>
                            <span className="network-search-result-username">
                              {person.headline || person.professions_str || person.industry || ''}
                            </span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Network Layout */}
        <div className="network-layout">
          {/* Sidebar */}
          <div className="network-sidebar">
            <div className="network-sidebar-card">
              <div className="network-sidebar-accent" />
              <div className="network-sidebar-body">
                <h3 className="network-sidebar-title">
                  Your Network.
                  <br />
                  Your Spotlight.
                </h3>
                <p className="network-sidebar-quote">
                  Your network is the center of your career. Take a chance — reach out to someone
                  new today.
                </p>
                <div className="network-sidebar-stats">
                  <div className="network-sidebar-stat">
                    <span className="network-sidebar-stat-num">
                      {followers.filter((f) => f.is_following_back).length}
                    </span>
                    <span className="network-sidebar-stat-label">Connections</span>
                  </div>
                  <div className="network-sidebar-stat">
                    <span className="network-sidebar-stat-num">{pendingConnections.length}</span>
                    <span className="network-sidebar-stat-label">Pending</span>
                  </div>
                  <div className="network-sidebar-stat">
                    <span className="network-sidebar-stat-num">{following.length}</span>
                    <span className="network-sidebar-stat-label">Following</span>
                  </div>
                </div>
                <Link to="/showcase" className="network-sidebar-link">
                  View Your Showcase →
                </Link>
                <Link to="/profile-settings" className="network-sidebar-link secondary">
                  Edit Portfolio
                </Link>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="network-main">
            <div className="network-content-card">
              <div className="network-tabs">
                <button
                  className={`network-tab ${networkTab === 'grow' ? 'active' : ''}`}
                  onClick={() => setNetworkTab('grow')}
                >
                  Grow
                  {suggestions.length + pendingConnections.length > 0 && (
                    <span className="network-tab-count">
                      {suggestions.length + pendingConnections.length}
                    </span>
                  )}
                </button>
                <button
                  className={`network-tab ${networkTab === 'catchup' ? 'active' : ''}`}
                  onClick={() => setNetworkTab('catchup')}
                >
                  Catch Up
                </button>
                <button
                  className={`network-tab ${networkTab === 'connections' ? 'active' : ''}`}
                  onClick={() => setNetworkTab('connections')}
                >
                  Connections
                </button>
              </div>

              <div className="network-tab-content">
                {networkTab === 'grow' && (
                  <>
                    {/* Connect Back */}
                    {pendingConnections.length > 0 && (
                      <div className="network-card-section">
                        <h3 className="network-card-section-title">
                          People who want to connect with you
                        </h3>
                        <div className="network-connection-grid">
                          {pendingConnections.map((person) => (
                            <div key={person.id} className="connection-card">
                              <div className="connection-card-banner" />
                              <Link
                                to={`/profile/${person.id}`}
                                className="connection-card-avatar-link"
                              >
                                <div className="connection-card-avatar">
                                  {person.profile_image ? (
                                    <img src={getMediaUrl(person.profile_image)} alt="" />
                                  ) : (
                                    person.full_name?.charAt(0) || '?'
                                  )}
                                </div>
                              </Link>
                              <div className="connection-card-body">
                                <Link to={`/profile/${person.id}`} className="connection-card-name">
                                  {person.full_name}
                                </Link>
                                {(person.headline || person.industry) && (
                                  <div className="connection-card-headline">
                                    {person.headline || person.industry}
                                  </div>
                                )}
                                {person.mutual_count > 0 && (
                                  <div className="connection-card-mutual">
                                    {person.mutual_count} mutual
                                  </div>
                                )}
                                {person.initial_message && (
                                  <div
                                    className="connection-card-initial-message"
                                    title={person.initial_message}
                                  >
                                    <span className="connection-card-initial-quote">“</span>
                                    {person.initial_message.length > 120
                                      ? person.initial_message.slice(0, 120).trimEnd() + '…'
                                      : person.initial_message}
                                    <span className="connection-card-initial-quote">”</span>
                                  </div>
                                )}
                              </div>
                              <div className="connection-card-actions">
                                <button
                                  className="connection-card-connect-btn connect-back"
                                  onClick={() => handleConnectBack(person)}
                                >
                                  Connect Back
                                </button>
                                <button
                                  className="connection-card-deny-btn"
                                  onClick={() => handleDenyPending(person)}
                                  title="Dismiss request"
                                >
                                  Dismiss
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Suggested */}
                    <div className="network-card-section">
                      <h3 className="network-card-section-title">People you may know</h3>
                      {suggestions.length > 0 ? (
                        <motion.div
                          className="network-connection-grid"
                          initial="hidden"
                          animate="visible"
                          variants={{ visible: { transition: { staggerChildren: 0.04 } } }}
                        >
                          {suggestions.map((person) => (
                            <motion.div
                              key={person.id}
                              className="connection-card"
                              variants={{
                                hidden: { opacity: 0, y: 16 },
                                visible: { opacity: 1, y: 0, transition: { duration: 0.25 } },
                              }}
                            >
                              <div className="connection-card-banner" />
                              <Link
                                to={`/profile/${person.id}`}
                                className="connection-card-avatar-link"
                              >
                                <div className="connection-card-avatar">
                                  {person.profile_image ? (
                                    <img src={getMediaUrl(person.profile_image)} alt="" />
                                  ) : (
                                    person.full_name?.charAt(0) || '?'
                                  )}
                                </div>
                              </Link>
                              <div className="connection-card-body">
                                <Link to={`/profile/${person.id}`} className="connection-card-name">
                                  {person.full_name}
                                </Link>
                                <div className="connection-card-headline">
                                  {person.headline ||
                                    person.professions?.slice(0, 2).join(' / ') ||
                                    ''}
                                </div>
                                {person.availability_status && (
                                  <div className="connection-card-status">
                                    <AvailabilityBadge status={person.availability_status} />
                                  </div>
                                )}
                                {person.reasons?.find((r) => r.type !== 'mutual') && (
                                  <div className="connection-card-reason">
                                    {person.reasons.find((r) => r.type !== 'mutual').text}
                                  </div>
                                )}
                                {person.mutual_count > 0 && (
                                  <div className="connection-card-mutual">
                                    {person.mutual_count} mutual connection
                                    {person.mutual_count !== 1 ? 's' : ''}
                                  </div>
                                )}
                              </div>
                              <button
                                className={`connection-card-connect-btn ${followedIds.has(person.id) ? 'connected' : ''}`}
                                onClick={() =>
                                  !followedIds.has(person.id) && openConnectModal(person)
                                }
                                disabled={followedIds.has(person.id)}
                              >
                                {followedIds.has(person.id) ? 'Connected' : '+ Connect'}
                              </button>
                            </motion.div>
                          ))}
                        </motion.div>
                      ) : (
                        !loading && (
                          <div className="network-empty-suggestions">
                            <p>No suggestions yet</p>
                            <p className="network-empty-hint">
                              As more people join Showcase, you'll see people you've worked with and
                              others in your network here.
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  </>
                )}

                {networkTab === 'catchup' && (
                  <div className="network-card-section">
                    <h3 className="network-card-section-title">
                      Stay in touch with your connections
                    </h3>
                    {(() => {
                      const mutual = followers.filter((f) => f.is_following_back);
                      return mutual.length > 0 ? (
                        <div className="network-connection-grid">
                          {mutual.map((person) => (
                            <div key={person.id} className="connection-card">
                              <div className="connection-card-banner" />
                              <Link
                                to={`/profile/${person.id}`}
                                className="connection-card-avatar-link"
                              >
                                <div className="connection-card-avatar">
                                  {person.profile_image ? (
                                    <img src={getMediaUrl(person.profile_image)} alt="" />
                                  ) : (
                                    person.full_name?.charAt(0) || '?'
                                  )}
                                </div>
                              </Link>
                              <div className="connection-card-body">
                                <Link to={`/profile/${person.id}`} className="connection-card-name">
                                  {person.full_name}
                                </Link>
                                <div className="connection-card-headline">
                                  {person.headline || person.industry || ''}
                                </div>
                              </div>
                              <Link
                                to={`/messages/${person.id}`}
                                className="connection-card-connect-btn message"
                              >
                                Message
                              </Link>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="network-empty-suggestions">
                          <p>Connect with people to see them here.</p>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {networkTab === 'connections' && (
                  <div className="network-card-section">
                    {(() => {
                      const mutual = followers.filter((f) => f.is_following_back);
                      const pending = following.filter((f) => !f.follows_back);
                      return (
                        <>
                          {mutual.length > 0 && (
                            <>
                              <h3 className="network-card-section-title">
                                Your Connections ({mutual.length})
                              </h3>
                              <div className="network-connection-grid">
                                {mutual.map((person) => (
                                  <div key={person.id} className="connection-card">
                                    <div className="connection-card-banner" />
                                    <Link
                                      to={`/profile/${person.id}`}
                                      className="connection-card-avatar-link"
                                    >
                                      <div className="connection-card-avatar">
                                        {person.profile_image ? (
                                          <img src={getMediaUrl(person.profile_image)} alt="" />
                                        ) : (
                                          person.full_name?.charAt(0) || '?'
                                        )}
                                      </div>
                                    </Link>
                                    <div className="connection-card-body">
                                      <Link
                                        to={`/profile/${person.id}`}
                                        className="connection-card-name"
                                      >
                                        {person.full_name}
                                      </Link>
                                      <div className="connection-card-headline">
                                        {person.headline || person.industry || ''}
                                      </div>
                                    </div>
                                    <Link
                                      to={`/messages/${person.id}`}
                                      className="connection-card-connect-btn message"
                                    >
                                      Message
                                    </Link>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                          {pending.length > 0 && (
                            <>
                              <h3
                                className="network-card-section-title"
                                style={{ marginTop: '24px' }}
                              >
                                Pending ({pending.length})
                              </h3>
                              <div className="network-connection-grid">
                                {pending.map((person) => (
                                  <div key={person.id} className="connection-card">
                                    <div className="connection-card-banner" />
                                    <Link
                                      to={`/profile/${person.id}`}
                                      className="connection-card-avatar-link"
                                    >
                                      <div className="connection-card-avatar">
                                        {person.profile_image ? (
                                          <img src={getMediaUrl(person.profile_image)} alt="" />
                                        ) : (
                                          person.full_name?.charAt(0) || '?'
                                        )}
                                      </div>
                                    </Link>
                                    <div className="connection-card-body">
                                      <Link
                                        to={`/profile/${person.id}`}
                                        className="connection-card-name"
                                      >
                                        {person.full_name}
                                      </Link>
                                      <div className="connection-card-headline">
                                        {person.headline || person.industry || ''}
                                      </div>
                                    </div>
                                    <button className="connection-card-connect-btn" disabled>
                                      Pending
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                          {mutual.length === 0 && pending.length === 0 && (
                            <div className="network-empty-suggestions">
                              <p>No connections yet. Start connecting!</p>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Network;
